import { clsx } from 'clsx'
import type { PointerEvent } from 'react'
import { useEffect, useState, useMemo } from 'react'

import clippyIcon from '{cImg}/clippy.svg'
import Tooltip from '~/components/Tooltip/index.tsx'
import copyTextToClipboard from '~/utils/copyTextToClipboard.ts'

import { Button } from './Button.tsx'
import doneIcon from './clippy--done.svg'
import styles from './CopyButton.module.scss'

const TIME_INTERVAL_FOR_VISUAL_FEEDBACK = 1500

interface CopyButtonProps {
  textToCopy: string
  buttonClassName?: string
  itemName?: string
  hasBox?: boolean
}

function CopyButton(props: CopyButtonProps) {
  const { textToCopy, buttonClassName, itemName, hasBox = false } = props
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>()
  const [visible, setVisible] = useState(false)
  const [clicked, setClicked] = useState(false)

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [timeoutId])

  const handleClick = () => {
    copyTextToClipboard(textToCopy)
    setClicked(true)

    const id = setTimeout(() => {
      setClicked(false)
      setVisible(false)
      setTimeoutId(undefined)
    }, TIME_INTERVAL_FOR_VISUAL_FEEDBACK)

    setTimeoutId(id)
  }

  const handlePointerEvent = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.type === 'pointerenter') {
      setVisible(true)
    } else if (event.type === 'pointerleave' && !timeoutId) {
      setVisible(false)
    }
  }

  const classNames = useMemo(
    () =>
      clsx(styles.copy, buttonClassName, {
        [styles.copied]: clicked,
        [styles.hasBox]: hasBox,
      }),
    [buttonClassName, clicked, hasBox],
  )

  return (
    <Tooltip
      className="tooltip--simple"
      content={clicked ? 'Copied' : 'Copy'}
      visible={visible}
      inertia
    >
      <Button
        variant="link"
        disabled={clicked}
        className={classNames}
        onPointerEnter={handlePointerEvent}
        onPointerLeave={handlePointerEvent}
        onClick={handleClick}
        aria-label={itemName ? `Copy ${itemName} to clipboard` : `Copy to clipboard`}
      >
        <img src={clippyIcon} alt="Clipboard" hidden={clicked} />
        <img src={doneIcon} alt="Green checkmark" hidden={!clicked} />
      </Button>
    </Tooltip>
  )
}

export default CopyButton
